import { createEmptyBlock } from 'lib-knowledge-document-structure';
import {
  findNode,
  getNodeString,
  insertNodes,
  removeNodes,
  setNodes,
  toDOMNode,
  withoutNormalizing,
} from 'lib-knowledge-document-structure/plate/common';
import {
  ELEMENT_IMAGE,
  ELEMENT_PARAGRAPH,
  MyEditor,
  MyImageElement,
} from 'lib-knowledge-document-structure/plateTypes';
import { Range as SlateRange } from 'lib-knowledge-document-structure/slate/package';
import { UploadFn } from 'lib-knowledge-management-common/hooks/useUploadImage';

import { imageUploadActions } from './imageUpload.store';

export type DropMode = 'replace' | 'below';

export const MAX_UPLOAD_FILES = 5;

const IMAGE_MIME_PREFIX = 'image/';
const HTML_TYPE = 'text/html';

function isImageMime(type: string): boolean {
  return type.startsWith(IMAGE_MIME_PREFIX);
}

export function getUploadableImageFiles(
  dataTransfer: DataTransfer,
): readonly File[] {
  if (dataTransfer.getData(HTML_TYPE)) {
    return [];
  }

  return [...(dataTransfer.files ?? [])].filter((file) =>
    isImageMime(file.type),
  );
}

export function hasUploadableImageDrag(dataTransfer: DataTransfer): boolean {
  if ([...(dataTransfer.types ?? [])].includes(HTML_TYPE)) {
    return false;
  }

  return [...(dataTransfer.items ?? [])].some(
    (item) => item.kind === 'file' && isImageMime(item.type),
  );
}

export function getSelectedBlockId(editor: MyEditor): string | undefined {
  if (!editor.selection) {
    return editor.children[editor.children.length - 1]?.id;
  }

  const [blockIndex] = SlateRange.end(editor.selection).path;

  return editor.children[blockIndex]?.id;
}

export function findBlockAtPoint(
  editor: MyEditor,
  clientX: number,
  clientY: number,
): { element: HTMLElement | undefined; id: string } | undefined {
  const pointed = document.elementFromPoint(clientX, clientY);
  const blockElements = editor.children.map((block) =>
    toDOMNode(editor, block),
  );
  const pointedIndex = pointed
    ? blockElements.findIndex((element) => element?.contains(pointed))
    : -1;
  const blockIndex =
    pointedIndex === -1
      ? findNearestIndexAbove(blockElements, clientY)
      : pointedIndex;
  const block = editor.children[blockIndex];

  return block
    ? { element: blockElements[blockIndex], id: block.id }
    : undefined;
}

function findNearestIndexAbove(
  blockElements: readonly (HTMLElement | undefined)[],
  clientY: number,
): number {
  let nearest = 0;

  for (let i = 0; i < blockElements.length; i++) {
    const blockTop = blockElements[i]?.getBoundingClientRect().top;

    if (blockTop === undefined || blockTop > clientY) {
      break;
    }

    nearest = i;
  }

  return nearest;
}

export function resolveDropMode(
  editor: MyEditor,
  blockId: string | undefined,
): DropMode {
  const block = editor.children.find(({ id }) => id === blockId);

  return block?.type === ELEMENT_PARAGRAPH && getNodeString(block).length === 0
    ? 'replace'
    : 'below';
}

export function insertImageFiles({
  blockId,
  editor,
  files,
  uploadImage,
}: {
  blockId: string | undefined;
  editor: MyEditor;
  files: readonly File[];
  uploadImage: UploadFn;
}): void {
  const placeholders = files.map(() => createEmptyBlock(ELEMENT_IMAGE));
  const blockIndex = editor.children.findIndex(({ id }) => id === blockId);
  const isReplacing = resolveDropMode(editor, blockId) === 'replace';
  const at =
    blockIndex === -1
      ? [editor.children.length]
      : [isReplacing ? blockIndex : blockIndex + 1];

  withoutNormalizing(editor, () => {
    if (isReplacing) removeNodes(editor, { at });

    insertNodes(editor, placeholders, { at });
  });

  placeholders.forEach(({ id }, index) => {
    imageUploadActions.start(id);
    fillUploadedImage({ editor, file: files[index], id, uploadImage });
  });
}

async function fillUploadedImage({
  editor,
  file,
  id,
  uploadImage,
}: {
  editor: MyEditor;
  file: File;
  id: string;
  uploadImage: UploadFn;
}): Promise<void> {
  const properties = await uploadImage(file).catch(() => undefined);

  const entry = findNode<MyImageElement>(editor, {
    at: [],
    match: (node) => node.id === id,
  });

  if (!entry) {
    imageUploadActions.clear(id);
    return;
  }

  if (!properties) {
    imageUploadActions.fail(id);
    return;
  }

  imageUploadActions.clear(id);

  setNodes<MyImageElement>(
    editor,
    {
      url: properties.url,
      filename: properties.filename,
      originalWidth: properties.width,
      originalHeight: properties.height,
    },
    { at: entry[1] },
  );
}
